/**
 * AgentModelChip — small pill rendered in the chat-slideout headers.
 *
 * Surfaces which LLM model + stage is currently driving the agent's
 * chat reasoning, with a click-through to LLM settings anchored to
 * that stage. Two related affordances:
 *
 *   <AgentModelChip stage="KNOWLEDGE_CHAT_AGENT" label="Claude Sonnet 4.5" />
 *     → a pill in the chat header; always visible.
 *
 *   <AgentModelErrorLink stage="KNOWLEDGE_CHAT_AGENT" />
 *     → a more verbose link rendered inside error banners
 *       ("Adjust this agent's model →"). Same destination.
 *
 * The destination is `/settings?tab=ai&sub=policy&stage=<stage>` — the Model policy sub of
 * the AI hub — which reads `stage` to auto-scroll + flash-highlight the row.
 *
 * Both render nothing unless an admin has revealed internals: naming the model
 * behind an agent is exactly the plumbing a customer should never meet, and the
 * destination is a tab they cannot open anyway.
 */
import { Link, useLocation } from 'react-router-dom';
import { useShowInternals } from '../../contexts/InternalsContext';
import { withReturnTo } from './returnPath';

interface AgentModelChipProps {
  stage: string;
  label?: string | null;
  /**
   * What to call the screen the user is leaving, for the settings page's
   * return button ("← Back to page builder"). The path is captured
   * automatically; this is only the human name. Omit for a plain "← Back".
   */
  returnLabel?: string;
}

/**
 * Where the user came from, carried through the settings URL so the settings
 * page can offer a way back. A model chip is a one-way door otherwise: you
 * click it from deep inside a page build, land in a settings tab, and the only
 * way home is the browser back button (which loses whatever tab-switching you
 * did once you got there).
 */
export interface ReturnTo {
  path: string; // in-app path incl. query string
  label?: string;
}

/** Append the return trail to a settings URL.
 *
 *  Delegates to the shared `withReturnTo` rather than repeating it. The private
 *  copy this replaces joined with a hardcoded `&`, so it only ever worked on a
 *  URL that already carried a `?` — true of both callers below, and a trap for
 *  the next one. The shared version picks the separator from the URL.
 */
function appendReturn(url: string, returnTo?: ReturnTo): string {
  return withReturnTo(url, returnTo?.path, returnTo?.label);
}

export function agentSettingsUrl(
  stage: string,
  returnTo?: ReturnTo,
  opts: { open?: boolean } = {},
): string {
  return appendReturn(
    `/settings?tab=ai&sub=policy&stage=${encodeURIComponent(stage)}${opts.open ? '&open=1' : ''}`,
    returnTo,
  );
}

/** Straight into a stage's edit dialog with one SEAT of its cast highlighted —
 *  what a persona row in the models chip links to, so "change the skeptic's
 *  model" is one click and lands on the skeptic. */
export function seatSettingsUrl(stage: string, seat: string, returnTo?: ReturnTo): string {
  return appendReturn(
    `/settings?tab=ai&sub=policy&stage=${encodeURIComponent(stage)}&open=1&seat=${encodeURIComponent(seat)}`,
    returnTo,
  );
}

/** The pipeline settings sub, with no particular stage singled out. */
export function pipelineSettingsUrl(returnTo?: ReturnTo): string {
  return appendReturn('/settings?tab=ai&sub=policy', returnTo);
}

/**
 * The current location as a ReturnTo — call from any component rendering a
 * link into settings so the trip is round-trip by default.
 */
export function useReturnToHere(label?: string): ReturnTo {
  const location = useLocation();
  return { path: `${location.pathname}${location.search}`, label };
}

export default function AgentModelChip({
  stage,
  label,
  returnLabel,
}: AgentModelChipProps) {
  const returnTo = useReturnToHere(returnLabel);
  const showInternals = useShowInternals();
  const displayLabel = label || 'not configured';
  const title = label
    ? `Model driving this agent - click to change`
    : `No model configured for the ${stage} stage - click to set one`;
  if (!showInternals) return null;
  return (
    <Link
      to={agentSettingsUrl(stage, returnTo)}
      title={title}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${
        label
          ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-indigo-300 dark:hover:border-indigo-700 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/30'
          : 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 hover:border-amber-500'
      }`}
    >
      <svg
        className="w-3 h-3 opacity-70"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
        />
      </svg>
      <span className="truncate max-w-[220px]">{displayLabel}</span>
    </Link>
  );
}

interface AgentModelErrorLinkProps {
  stage: string;
  returnLabel?: string;
}

export function AgentModelErrorLink({
  stage,
  returnLabel,
}: AgentModelErrorLinkProps) {
  const returnTo = useReturnToHere(returnLabel);
  // The remedy is only a remedy for someone who can reach pipeline settings, and
  // that tab is admin-only now. For everyone else the error text stands alone.
  const showInternals = useShowInternals();
  if (!showInternals) return null;
  return (
    <Link
      to={agentSettingsUrl(stage, returnTo)}
      className="inline-flex items-center gap-1 ml-1 underline hover:no-underline"
      title="Open this agent's stage in LLM settings"
    >
      Adjust this agent's model →
    </Link>
  );
}
